import { Button, Checkbox, FormField, Select, TextField } from "@smooth/design-system-components"
import { Panel, formatYen } from "../../components/shared"
import { scenarioContactCount, scenarioPrincipalDue } from "../caseFacts"
import { UNSET_LABEL, UNSET_TERMINAL, isUnsetTerminal, terminalShortLabel } from "../../terminals"
import { terminalOptionLabel } from "../types"
import type { EvaluationCase, Scenario, Terminals } from "../types"
import { inReleaseCheck } from "../origin"
import type { ValidatedField } from "../validation"
import { CaseFrame, CaseHeader, HEADER_BUTTON_CLASS, ReviewCard } from "./CaseFrame"
import type { CaseFrameSlots } from "./CaseFrame"
import { caseSectionCounts } from "./CaseReadView"
import { ExpectedPathField } from "./ExpectedPath"
import { ExpectationJsonField } from "./ExpectationView"
import { modelExemplars, retargetExpectation, withModelExemplar } from "../expectation"
import {
  ContactLogEditor,
  ContractEditor,
  FIELD_GRID,
  MemoEditor,
  PromiseEditor,
} from "./ScenarioRecordsForm"
import { ConversationEditor } from "./ConversationEditor"
import { AutoGrowTextarea, ReadOnlyField } from "./parts"

/** 架電不可の相手。AI 入力の「架電不可:」の行と同じ並び */
const NO_CALL_TARGETS = [
  { key: "noCallToUser", label: "本人" },
  { key: "noCallToWorkplace", label: "勤務先" },
  { key: "noCallToEmergencyContact", label: "緊急連絡先" },
] as const

function noCallLabel(scenario: Scenario): string {
  const targets = NO_CALL_TARGETS.filter(({ key }) => scenario[key]).map(({ label }) => label)
  return targets.length ? targets.join("・") : "なし"
}

type Props = {
  record: EvaluationCase
  terminals: Terminals
  errors: Partial<Record<ValidatedField, string>>
  /** 保存・複製の実行中など、操作を受け付けない間は true */
  busy: boolean
  /** 未保存の変更があるか（既存ケースは変更がないと保存できない） */
  changed: boolean
  onChange: (patch: Partial<EvaluationCase>) => void
  onSave: () => void
  /** 変更がないときに編集をやめる（押せない保存ボタンを出さない） */
  onClose?: () => void
} & CaseFrameSlots

/**
 * ケースの編集フォーム。読み取り表示と同じ外枠・並び（督促ケース詳細と同じ）のまま、各パネルを入力欄にする。
 * ケース名・時点・送るきっかけはヘッダーで、期待する対応・お手本の文・メモは右の先頭の「期待」で直す（どれも AI には渡さない）。
 * 編集欄は AI の判断か評価に効くものだけにする。
 * AI には渡るが読み込んだ値のままでよいものは表示だけ、どこにも効かないものは出さない。
 * 出さない・表示だけの項目も保存済みの値は record にそのまま残り、保存で送り直す
 */
export function CaseEditForm({
  record,
  terminals,
  errors,
  busy,
  changed,
  onChange,
  onSave,
  onClose,
  ...slots
}: Props) {
  const invalid = Object.keys(errors).length > 0
  const scenario = (patch: Partial<Scenario>) =>
    onChange({ scenario: { ...record.scenario, ...patch } })
  const hasContracts = !!record.scenario.contracts?.length

  return (
    <CaseFrame
      resetKey={`edit-${record.id}`}
      header={
        <CaseHeader
          record={record}
          editing={{ errors, onChange }}
          {...slots}
          actions={
            <>
              {slots.actions}
              {!changed && record.id && onClose ? (
                <Button
                  variant="outline"
                  className={HEADER_BUTTON_CLASS}
                  onClick={onClose}
                  disabled={busy}
                >
                  閉じる
                </Button>
              ) : (
                <Button className={HEADER_BUTTON_CLASS} onClick={onSave} disabled={busy || invalid}>
                  保存
                </Button>
              )}
            </>
          }
        />
      }
      counts={caseSectionCounts(record.scenario)}
      chat={
        <ConversationEditor
          conversation={record.scenario.conversation}
          asOf={record.scenario.asOf}
          busy={busy}
          error={errors.conversation}
          onChange={(conversation) => scenario({ conversation })}
        />
      }
      rightTop={
        <>
          {slots.rightHead}
          <ReviewCard>
            <div className="@container flex flex-col gap-4">
              <div className={FIELD_GRID}>
                <FormField label="期待する対応" error={errors.expectedTerminal}>
                  <Select
                    value={record.expectedTerminal}
                    error={!!errors.expectedTerminal}
                    onChange={(e) =>
                      onChange({
                        expectedTerminal: e.target.value,
                        expectation: retargetExpectation(
                          record.expectation,
                          record.expectedTerminal,
                          e.target.value
                        ),
                      })
                    }
                    // 督促ケースから読み込んだ下書きは未選択で始まる
                    placeholder="選んでください"
                    options={[
                      ...Object.entries(terminals).map(([id, label]) => ({
                        value: id,
                        label: terminalOptionLabel(id, label),
                      })),
                      // 表示名の一覧に無い着地点（標準ケース・読み込んだケースで付いたもの）も選んだまま出す
                      ...(record.expectedTerminal &&
                      !(record.expectedTerminal in terminals) &&
                      !isUnsetTerminal(record.expectedTerminal)
                        ? [
                            {
                              value: record.expectedTerminal,
                              label: terminalShortLabel(record.expectedTerminal),
                            },
                          ]
                        : []),
                      ...(inReleaseCheck(record) && !isUnsetTerminal(record.expectedTerminal)
                        ? []
                        : [{ value: UNSET_TERMINAL, label: UNSET_LABEL }]),
                    ]}
                  />
                </FormField>
              </div>
              <FormField label="お手本の文（オペレーターが送った文）">
                <AutoGrowTextarea
                  maxLength={5000}
                  value={modelExemplars(record.expectation)[0]?.body ?? ""}
                  onChange={(e) =>
                    onChange({
                      expectation: withModelExemplar(
                        record.expectation,
                        e.target.value,
                        record.expectedTerminal
                      ),
                    })
                  }
                />
              </FormField>
              <FormField label="メモ">
                <AutoGrowTextarea
                  maxLength={5000}
                  value={record.checkpoints}
                  onChange={(e) => onChange({ checkpoints: e.target.value })}
                />
              </FormField>
              <details
                aria-label="詳しい期待"
                className="rounded-md border border-border-default px-3 py-2"
              >
                <summary className="cursor-pointer text-body font-bold text-text-default">
                  詳しい期待
                </summary>
                <div className="flex flex-col gap-4 pt-3">
                  <ExpectedPathField
                    path={record.scenario.expectedPath}
                    onChange={(expectedPath) => scenario({ expectedPath })}
                  />
                  <ExpectationJsonField
                    // 期待する対応を変えたら、許す対応の先頭を差し替えた内容で書き直す
                    key={record.expectedTerminal}
                    expectation={record.expectation}
                    expectedTerminal={record.expectedTerminal}
                    onChange={(expectation) => onChange({ expectation })}
                  />
                </div>
              </details>
            </div>
            {slots.review}
          </ReviewCard>
          <div className="@container">
            <PromiseEditor
              scenario={record.scenario}
              busy={busy}
              error={errors.activePromise ?? errors.promiseHistory}
              onChange={scenario}
            />
          </div>
        </>
      }
      sections={{
        user: (
          <Panel title="ユーザー情報">
            <div className="@container">
              <div className={FIELD_GRID}>
                <FormField label="勤務先">
                  <TextField
                    value={record.scenario.companyName}
                    onChange={(e) => scenario({ companyName: e.target.value })}
                  />
                </FormField>
                <FormField label="給料日（毎月・未確認なら空欄）" error={errors.payday}>
                  <TextField
                    type="number"
                    min={1}
                    max={31}
                    error={!!errors.payday}
                    value={record.scenario.payday ?? ""}
                    onChange={(e) =>
                      scenario({ payday: e.target.value ? Number(e.target.value) : null })
                    }
                  />
                </FormField>
              </div>
            </div>
            <div className="pt-3">
              <ReadOnlyField label="LINEブロック">
                {record.scenario.lineBlocked ? "あり" : "なし"}
              </ReadOnlyField>
            </div>
            <div className="pt-3">
              <ReadOnlyField label="口座振替の登録">
                {record.scenario.bankAccountRegistered ? "あり" : "なし"}
              </ReadOnlyField>
            </div>
            <div className="pt-3">
              <ReadOnlyField label="架電不可">{noCallLabel(record.scenario)}</ReadOnlyField>
            </div>
            <details
              aria-label="詳しい設定 ユーザー情報"
              className="mt-3 rounded-md border border-border-default px-3 py-2"
            >
              <summary className="cursor-pointer text-caption text-text-default">
                詳しい設定（LINEブロック・口座振替の登録・架電不可を変える）
              </summary>
              <div className="pt-3">
                <Checkbox
                  label="LINEブロックあり"
                  checked={!!record.scenario.lineBlocked}
                  onChange={(e) => scenario({ lineBlocked: e.target.checked })}
                />
              </div>
              <div className="pt-3">
                <Checkbox
                  label="口座振替の登録あり"
                  checked={!!record.scenario.bankAccountRegistered}
                  onChange={(e) => scenario({ bankAccountRegistered: e.target.checked })}
                />
              </div>
              {NO_CALL_TARGETS.map(({ key, label }) => (
                <div key={key} className="pt-3">
                  <Checkbox
                    label={`${label}への架電不可`}
                    checked={!!record.scenario[key]}
                    onChange={(e) => scenario({ [key]: e.target.checked })}
                  />
                </div>
              ))}
            </details>
          </Panel>
        ),
        delinquency: (
          <Panel title="遅延状況">
            <div className="@container">
              <div className={FIELD_GRID}>
                <FormField label="遅延日数" error={errors.overdueDays}>
                  <TextField
                    type="number"
                    min={0}
                    max={100_000_000}
                    error={!!errors.overdueDays}
                    value={record.scenario.overdueDays}
                    onChange={(e) => scenario({ overdueDays: Number(e.target.value) })}
                  />
                </FormField>
                <FormField
                  label="遅延金額（円）"
                  error={errors.principalDue}
                  hint={hasContracts ? "契約の分割予定から計算します。" : undefined}
                >
                  <TextField
                    type="number"
                    min={0}
                    max={100_000_000}
                    error={!!errors.principalDue}
                    readOnly={hasContracts}
                    value={
                      hasContracts
                        ? scenarioPrincipalDue(record.scenario)
                        : record.scenario.principalDue
                    }
                    onChange={(e) => scenario({ principalDue: Number(e.target.value) })}
                  />
                </FormField>
                <ReadOnlyField
                  label="遅延損害金"
                  // 直す欄は「詳しい設定」の中なので、閉じていても見えるここに理由を出す
                  error={errors.lateChargeDue}
                >
                  {formatYen(record.scenario.lateChargeDue)}
                </ReadOnlyField>
                <ReadOnlyField
                  label="これまでの接触回数"
                  error={errors.contactCountTotal}
                  hint={
                    record.scenario.contactCountTotal == null
                      ? "当社の送信と交渉記録から数えた値です。"
                      : "指定した値です（督促ケースから読み込んだケースは読み込んだ時点の回数）。会話を編集しても変わりません。"
                  }
                >
                  {`${scenarioContactCount(record.scenario)}回`}
                </ReadOnlyField>
              </div>
              <details
                aria-label="詳しい設定 遅延状況"
                className="mt-3 rounded-md border border-border-default px-3 py-2"
              >
                <summary className="cursor-pointer text-caption text-text-default">
                  詳しい設定（遅延損害金・接触回数を直接指定する）
                </summary>
                <div className="grid gap-4 pt-3 min-[600px]:grid-cols-2">
                  <FormField
                    label="遅延損害金の指定（円）"
                    hint="1円以上なら AI 入力に遅延損害金の行が出ます。"
                  >
                    <TextField
                      type="number"
                      min={0}
                      max={100_000_000}
                      error={!!errors.lateChargeDue}
                      value={record.scenario.lateChargeDue}
                      onChange={(e) => scenario({ lateChargeDue: Number(e.target.value) })}
                    />
                  </FormField>
                  <FormField
                    label="接触回数の指定"
                    hint="空欄なら当社の送信と交渉記録から数えます。"
                  >
                    <TextField
                      type="number"
                      min={0}
                      max={100_000}
                      error={!!errors.contactCountTotal}
                      value={record.scenario.contactCountTotal ?? ""}
                      onChange={(e) =>
                        scenario({
                          contactCountTotal:
                            e.target.value.trim() === "" ? null : Number(e.target.value),
                        })
                      }
                    />
                  </FormField>
                </div>
              </details>
            </div>
          </Panel>
        ),
        contracts: (
          <div className="@container">
            <ContractEditor
              scenario={record.scenario}
              busy={busy}
              error={errors.contracts}
              onChange={scenario}
            />
          </div>
        ),
        memos: (
          <div className="@container">
            <MemoEditor
              scenario={record.scenario}
              busy={busy}
              error={errors.operatorMemos}
              onChange={scenario}
            />
          </div>
        ),
        contact_logs: (
          <div className="@container">
            <ContactLogEditor
              scenario={record.scenario}
              busy={busy}
              error={errors.contactLogs}
              onChange={scenario}
            />
          </div>
        ),
      }}
    />
  )
}
